import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Bank, ShieldCheck } from "@phosphor-icons/react";

export default function AdminBankAccount() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    account_holder: "", bank_name: "", routing_number: "",
    account_number: "", swift_code: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get("/admin/bank-account").then((r) => {
      setData(r.data);
      if (r.data?.configured) {
        setForm({
          account_holder: r.data.account_holder || "",
          bank_name: r.data.bank_name || "",
          routing_number: r.data.routing_number || "",
          account_number: "",
          swift_code: r.data.swift_code || "",
          notes: r.data.notes || "",
        });
      }
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.account_holder || !form.bank_name || !form.routing_number || !form.account_number) {
      toast.error("Account holder, bank, routing, and account number are all required.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/admin/bank-account", form);
      toast.success("Bank account saved");
      const r = await api.get("/admin/bank-account");
      setData(r.data);
      setForm((f) => ({ ...f, account_number: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="gf-overline">Admin · Receiving Bank</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
        Payout account.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl text-sm leading-relaxed">
        The receiving bank account where GrantFlow's subscription &amp; template revenue is paid
        out. Account number is encrypted at rest with Fernet (AES-128-CBC).
      </p>

      {data?.configured && (
        <div data-testid="bank-current" className="mt-6 border border-zinc-200 bg-zinc-50 p-5">
          <div className="flex items-center gap-2 gf-overline">
            <ShieldCheck size={14} /> Current account on file
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm font-mono">
            <div><span className="text-zinc-500">Holder:</span> {data.account_holder}</div>
            <div><span className="text-zinc-500">Bank:</span> {data.bank_name}</div>
            <div><span className="text-zinc-500">Routing:</span> {data.routing_number}</div>
            <div><span className="text-zinc-500">Account:</span> {data.account_number_masked}</div>
            <div><span className="text-zinc-500">SWIFT:</span> {data.swift_code || "—"}</div>
            <div><span className="text-zinc-500">Updated:</span> {data.updated_at?.slice(0, 10)}</div>
          </div>
        </div>
      )}

      <form onSubmit={save} className="mt-6 border border-zinc-200 bg-white p-6" data-testid="bank-form">
        <div className="flex items-center gap-2 mb-4">
          <Bank size={18} />
          <div className="gf-overline">{data?.configured ? "Update account" : "Add account"}</div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="gf-overline">Account holder</Label>
            <Input data-testid="bank-holder" value={form.account_holder} onChange={up("account_holder")}
              className="gf-sharp mt-2 border-zinc-300 bg-white" />
          </div>
          <div>
            <Label className="gf-overline">Bank name</Label>
            <Input data-testid="bank-name" value={form.bank_name} onChange={up("bank_name")}
              className="gf-sharp mt-2 border-zinc-300 bg-white" />
          </div>
          <div>
            <Label className="gf-overline">Routing number</Label>
            <Input data-testid="bank-routing" value={form.routing_number} onChange={up("routing_number")}
              className="gf-sharp mt-2 border-zinc-300 bg-white font-mono" />
          </div>
          <div>
            <Label className="gf-overline">Account number</Label>
            <Input data-testid="bank-account" type="password" value={form.account_number}
              onChange={up("account_number")} placeholder={data?.configured ? "•••• re-enter to update" : ""}
              className="gf-sharp mt-2 border-zinc-300 bg-white font-mono" />
          </div>
          <div>
            <Label className="gf-overline">SWIFT / BIC (optional)</Label>
            <Input data-testid="bank-swift" value={form.swift_code} onChange={up("swift_code")}
              className="gf-sharp mt-2 border-zinc-300 bg-white font-mono" />
          </div>
          <div className="md:col-span-2">
            <Label className="gf-overline">Notes</Label>
            <Textarea data-testid="bank-notes" rows={2} value={form.notes} onChange={up("notes")}
              className="gf-sharp mt-2 border-zinc-300 bg-white" />
          </div>
        </div>
        <Button type="submit" disabled={busy} data-testid="bank-save"
          className="gf-sharp mt-5 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide">
          {busy ? "Saving…" : data?.configured ? "Update account" : "Save account"}
        </Button>
      </form>
    </div>
  );
}
